import React from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "./TailwindClassnames";
import { useProfile } from "./ProfileProvider";

type SelectInputProps = {
  options: Array<any>;
  className: string;
  defaultValue?: object | null;
  value?: object | null;
  isDisabled: boolean;
  isLoading: boolean;
  isClearable: boolean;
  isSearchable: boolean;
  isMulti: boolean;
  name: string;
  errors: any;
  onChange: Function;
  onMenuScrollToBottom?: any;
  onKeyDown?: any;
  searchBar: boolean;
  isAsync?: boolean;
  loadOptions?: (
    inputValue: string,
    callback: (options: any[]) => void,
  ) => void;
  /**
   * Text of the open menu when there is nothing to list. Omitted, the async
   * select falls back to the translated "No options available" and the plain
   * select keeps react-select's built-in (untranslated) "No options", exactly
   * as before AKL-848 — so no existing call site changes unless it opts in.
   */
  noOptionsMessage?: (obj: { inputValue: string }) => string;
  placeholder?: string;
  /**
   * Options the async menu shows while the search input is **empty** — react-
   * select resolves its menu as
   * `inputValue ? loadedOptions : defaultOptions`, so these are a starting
   * point that the first keystroke replaces with `loadOptions`' results, not a
   * filter over them.
   *
   * Defaults to `[]`, which is the value this component hardcoded before
   * AKL-781 — so an async select stays blank until the reviewer types unless a
   * caller opts in. Added for the review surface's species picker, which seeds
   * the menu with the taxa already on the suggestion cards being reassigned.
   * Ignored entirely unless `isAsync` is on.
   */
  defaultOptions?: Array<any>;
  /**
   * Renders the open menu into a portal on `document.body` instead of in its
   * normal DOM position. Opt-in and off by default so existing call sites are
   * unaffected — turn it on for a select whose menu gets clipped by an
   * ancestor's `overflow-hidden`/`overflow-auto` (e.g. a table toolbar card),
   * since a portaled menu escapes that ancestor's clipping entirely.
   */
  usePortal?: boolean;
  /**
   * DOM id put on react-select's own combobox input, which also becomes the
   * `htmlFor` of the label this component already renders from `name`.
   *
   * Without it the label is a bare `<label>` bound to nothing and react-select
   * generates its own internal input id, so the combobox has **no accessible
   * name** — a screen reader announces it as "combobox" and nothing else. This
   * threads an id through rather than adding an `aria-label` prop so the
   * visible label and the accessible name stay the same words by construction.
   *
   * Opt-in and `undefined` by default: omitted, the label renders without
   * `htmlFor` and react-select keeps its generated id, exactly as before
   * AKL-811. Nothing about the styling or behaviour of any existing call site
   * changes.
   */
  inputId?: string;
  /**
   * Content rendered immediately after the visible label — an info button, a
   * badge — in a flex row beside it.
   *
   * Note the difference from `TextInput`, which renders its `labelSuffix`
   * **inside** `<label htmlFor>`. That is deliberate and must not be
   * "made consistent": this component's label is bound to react-select's
   * combobox, so anything nested in it would be folded into the combobox's
   * accessible name, and a click on an interactive suffix would activate the
   * select through label forwarding. Rendering it as a sibling avoids both.
   *
   * Omitted, the label renders exactly as before — a single bare `<label>`
   * with no wrapper — so no existing call site changes.
   */
  labelSuffix?: React.ReactNode;
  /**
   * Marks the combobox as required for assistive technology: react-select puts
   * `aria-required` on its input. It adds no visible marker — the caller's
   * `name` carries the "*" as elsewhere in the app.
   *
   * Opt-in and `undefined` by default, so no existing call site changes
   * (AKL-977).
   */
  required?: boolean;
};

const SelectInput: React.FC<SelectInputProps> = ({
  options,
  className,
  defaultValue,
  value,
  isDisabled,
  isLoading,
  isClearable,
  isSearchable,
  isMulti,
  name,
  errors,
  onChange,
  onMenuScrollToBottom,
  onKeyDown,
  searchBar,
  isAsync = false,
  loadOptions,
  noOptionsMessage,
  placeholder,
  usePortal = false,
  defaultOptions = [],
  inputId,
  labelSuffix,
  required,
}) => {
  const { theme } = useProfile();
  const isDarkMode = theme === "dark";
  const { t } = useTranslation();

  // Dark-mode resting border. Filter toolbars sit on a card and use the
  // hairline border their neighbouring filter inputs use (storm-blue-border);
  // form selects keep the heavier off-black-light border of the text inputs
  // they line up with.
  const restingDarkBorder = searchBar ? "#33343F" : "#626262";

  const customStyles = {
    option: (provided: any, state: any) => ({
      ...provided,
      color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
      backgroundColor: searchBar
        ? isDarkMode
          ? "#272837"
          : "#FFFFFF"
        : isDarkMode
          ? "#272837"
          : "#FFFFFF",
      textAlign: "left",
      ":hover": {
        backgroundColor: isDarkMode ? "#6B7280" : "#B0E6FA",
        color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: searchBar
        ? isDarkMode
          ? "#272837"
          : "#FFFFFF"
        : isDarkMode
          ? "#272837"
          : "#FFFFFF",
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      padding: "0px 1px",
      color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
      // A disabled control reads as "off" through a light grey fill plus greyed
      // text, not through opacity — the 0.45 wash this used to carry faded the
      // border and indicators along with it, leaving the control barely visible
      // against a card of the same background color.
      backgroundColor: state.isDisabled
        ? isDarkMode
          ? "#22232F"
          : "#F5F5F5"
        : isDarkMode
          ? "#272837"
          : "#FFFFFF",
      // Disabled controls keep a muted border instead of "none" so they still
      // read as a control when sitting on a card of the same background color.
      border: `1px solid ${
        state.isDisabled
          ? isDarkMode
            ? "#33343F"
            : "#E5E5E5"
          : isDarkMode
            ? restingDarkBorder
            : "#D8D8D8"
      }`,
      // Solid focus ring matching the Tailwind inputs beside it
      // (ring-storm-blue / dark:ring-sky-blue); the earlier 25 % teal composited
      // to ~1.3:1 against white and was effectively invisible (AKL-694, F6).
      boxShadow:
        state.isFocused && !state.isDisabled
          ? `0 0 0 2px ${isDarkMode ? "#B0E6FA" : "#272837"}`
          : "none",
      // Hover keeps the resting border color — react-select's default hover
      // border is overridden here, and a white hover border disappeared against
      // the white card in light mode. Text inputs have no hover border change
      // either, so selects stay consistent with them.
      "&:hover": {
        border: state.isDisabled
          ? `1px solid ${isDarkMode ? "#33343F" : "#E5E5E5"}`
          : `1px solid ${isDarkMode ? restingDarkBorder : "#D8D8D8"}`,
      },
    }),
    input: (provided: any, state: any) => ({
      ...provided,
      color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
    }),
    placeholder: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled
        ? isDarkMode
          ? "#999999"
          : "#999999" // Greyed text for disabled
        : "#6E6E7F",
    }),
    // The arrow and separator are greyed explicitly when disabled — they used
    // to be muted by the control's blanket 0.45 opacity, which no longer
    // applies now that the disabled state is expressed as a grey fill.
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled ? "#999999" : isDarkMode ? "#FFFFFF" : "#2A2A2A",
      ":hover": {
        color: state.isDisabled
          ? "#999999"
          : isDarkMode
            ? "#FFFFFF"
            : "#6C939E",
      },
    }),
    indicatorSeparator: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isDisabled
        ? isDarkMode
          ? "#3F3F52"
          : "#D8D8D8"
        : isDarkMode
          ? "#6B7280"
          : "#272837",
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      color: isDarkMode ? "#FFFFFF" : "#272837",
      ":hover": {
        color: isDarkMode ? "#FFFFFF" : "#6C939E",
      },
    }),
    // react-select has no built-in dark-mode awareness for selected-item
    // chips (isMulti) — without an override here they render its light-grey
    // default in both themes, which stands out against every dark-mode
    // control on the page.
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: isDarkMode ? "#6E6E7F" : "#EDEDED",
      border: `1px solid ${isDarkMode ? "#33343F" : "#D8D8D8"}`,
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: isDarkMode ? "#FFFFFF" : "#272837",
      ":hover": {
        backgroundColor: isDarkMode ? "#6B7280" : "#B0E6FA",
        color: isDarkMode ? "#FFFFFF" : "#2A2A2A",
      },
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled
        ? isDarkMode
          ? "#999999"
          : "#999999" // Greyed text for disabled
        : isDarkMode
          ? "#FFFFFF"
          : "#2A2A2A",
    }),
    // Only rendered when `usePortal` is on — the portaled menu escapes into
    // `document.body`'s stacking context, so it needs its own z-index to
    // clear fixed headers/table cards. Matches the app's standard modal tier
    // (z-50, see TailwindClassnames' modalOverlay) rather than an arbitrary
    // large number, so a portaled menu can't paint above a genuine modal or
    // the review page's higher-tier overlays (z-[1200]/z-[1300]).
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 50,
    }),
  };

  return (
    <div className="flex flex-col items-start">
      {labelSuffix ? (
        /*
          `dropdownLabel` ends in `mb-1`. Centring a flex row centres each
          child's margin box, so that 4px of bottom margin sits the suffix
          4px lower than the label text it is meant to line up with. The
          margin moves to this row and is cancelled on the label, which keeps
          the spacing below the label identical to every other call site
          without touching the shared class.
        */
        <span className="mb-1 flex items-center gap-1 [&>label]:mb-0">
          <label className={tailwindClassnames.dropdownLabel} htmlFor={inputId}>
            {name}
          </label>
          {labelSuffix}
        </span>
      ) : (
        <label className={tailwindClassnames.dropdownLabel} htmlFor={inputId}>
          {name}
        </label>
      )}
      {isAsync && loadOptions ? (
        <AsyncSelect
          inputId={inputId}
          required={required}
          className={className}
          defaultValue={defaultValue}
          value={value}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isSearchable={isSearchable}
          isMulti={isMulti}
          name={name}
          onChange={(e) => onChange(e)}
          loadOptions={loadOptions}
          placeholder={placeholder || t("Select...")}
          onKeyDown={onKeyDown}
          styles={customStyles}
          menuPortalTarget={usePortal ? document.body : undefined}
          menuPosition={usePortal ? "fixed" : undefined}
          noOptionsMessage={
            noOptionsMessage || (() => t("No options available"))
          }
          loadingMessage={() => t("Loading...")}
          cacheOptions
          defaultOptions={defaultOptions}
        />
      ) : (
        <Select
          inputId={inputId}
          required={required}
          options={options}
          className={className}
          defaultValue={defaultValue}
          value={value}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isSearchable={isSearchable}
          isMulti={isMulti}
          name={name}
          onChange={(e) => onChange(e)}
          onMenuScrollToBottom={onMenuScrollToBottom}
          placeholder={placeholder || t("Select...")}
          onKeyDown={onKeyDown}
          styles={customStyles}
          menuPortalTarget={usePortal ? document.body : undefined}
          menuPosition={usePortal ? "fixed" : undefined}
          // `undefined` leaves react-select's own default in place, so callers
          // that do not pass this see no change.
          noOptionsMessage={noOptionsMessage}
          // While `isLoading` an empty menu shows this instead of
          // `noOptionsMessage`; react-select's default is English-only.
          loadingMessage={() => t("Loading...")}
        />
      )}
      {errors && (
        <p className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2 my-1">
          {errors}
        </p>
      )}
    </div>
  );
};

export default SelectInput;
